<script setup lang="ts">
import QrcodeVue from 'qrcode.vue'

import type { CodeType, Wallet } from '@/types/wallet'

const props = defineProps<{
  wallet: Wallet
  codeType: CodeType
}>()

const { user } = useLocalStorage()
const { t } = useI18n()

const selectedDevice = ref<'ios' | 'android'>('ios')
const selectedView = ref<'wallet' | 'details'>('wallet')

const backgroundColor = computed(() => {
  if (selectedView.value === 'wallet') {
    return props.wallet.fields['backgroundColor']
  }

  return selectedDevice.value === 'ios' ? '#1C1C1D' : '#FFFFFF'
})

const textColor = computed(() => {
  if (selectedView.value === 'wallet') {
    return props.wallet.fields['iosTitleColor']
  }

  return selectedDevice.value === 'ios' ? '#FFFFFF' : '#000000'
})

const openUrl = (url: string) => {
  window.open(url, '_blank')
}
</script>

<template>
  <div class="mt-8 h-max rounded-md border bg-white xl:sticky xl:top-32 xl:mt-0 xl:self-start xl:overflow-auto">
    <div class="sticky top-0 z-20 flex gap-5 rounded-t-md border-b bg-white p-4 text-foreground">
      <BaseIcon name="HeroEyeIconSolid" class="w-5" />
      <p>{{ t('pages.loyaltySupports.wallet.config.preview.title') }}</p>
    </div>

    <div class="my-6 flex justify-center">
      <Tabs v-model="selectedDevice">
        <TabsList>
          <TabsTrigger value="ios">{{ t('pages.loyaltySupports.wallet.config.preview.ios') }}</TabsTrigger>
          <TabsTrigger value="android">{{ t('pages.loyaltySupports.wallet.config.preview.android') }}</TabsTrigger>
        </TabsList>
      </Tabs>
    </div>

    <div
      v-if="selectedDevice === 'ios'"
      class="mx-auto h-[450px] w-[300px] rounded-3xl shadow"
      :style="{ backgroundColor: backgroundColor, color: textColor }"
    >
      <div v-if="selectedView === 'wallet'" class="flex h-full flex-col">
        <div class="p-4">
          <img :src="wallet.fields['iosLogo']" class="mr-4 h-7 w-7 object-cover" alt="Logo wallet" />
        </div>
        <div
          class="flex-1 bg-white bg-cover bg-center bg-no-repeat"
          :style="{ backgroundImage: 'url(' + wallet.fields['iosImage'] + ')' }"
        ></div>
        <div class="space-y-10 px-4 pb-4 pt-2">
          <div class="flex flex-col">
            <span class="text-xs uppercase">{{ wallet.fields['iosPointsTitle'] }}</span>
            10
          </div>
          <div
            :class="codeType.class"
            class="mx-auto flex flex-col items-center bg-white p-2 text-center text-black shadow-md"
          >
            <div v-if="codeType.name === 'barcode' || !wallet.testUrl" class="flex flex-1 items-center justify-center">
              {{ t(`pages.loyaltySupports.wallet.config.${codeType.name}`) }}
            </div>
            <div v-else class="flex flex-1 items-center justify-center">
              <qrcode-vue :value="wallet.testUrl" :size="100" level="L" />
            </div>
            <span v-if="codeType.name === 'barcode'" class="text-xs">cust_1a2b3c4d5e6f789</span>
            <span v-else-if="codeType.name === 'qrcode' && wallet.testUrl" class="text-xs">
              {{ t(`pages.loyaltySupports.wallet.config.scanMe`) }}
            </span>
          </div>
        </div>
      </div>
      <div v-else-if="selectedView === 'details'" class="p-4">
        <div class="mb-6 text-center font-medium">{{ wallet.fields['merchantName'] }}</div>
        <img :src="wallet.fields['iosLogo']" class="mx-auto h-14 w-20 object-cover" alt="Logo wallet" />
        <div class="mb-10 mt-2 text-center text-xl font-bold">{{ wallet.fields['merchantName'] }}</div>
        <div class="rounded-xl bg-white/10 px-3 py-2">
          <div class="space-y-1.5">
            <p class="text-xs font-medium">{{ wallet.fields['rewardsTitle'] }}</p>
            <p class="text-sm text-white/70">{{ user.merchant.webAppUrl }}</p>
          </div>
          <hr class="my-3 border-t border-white/10" />
          <div class="space-y-1.5">
            <p class="text-xs font-medium">{{ t('pages.loyaltySupports.wallet.config.preview.lastNotification') }}</p>
            <p class="text-sm text-white/70">
              {{ t('pages.loyaltySupports.wallet.config.preview.lastNotificationContent') }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="selectedDevice === 'android'"
      class="mx-auto h-[450px] w-[300px] rounded-3xl shadow"
      :style="{ backgroundColor: backgroundColor, color: textColor }"
    >
      <div v-if="selectedView === 'wallet'" class="flex h-full flex-col">
        <div class="flex items-center border-b border-white/20 p-4">
          <img
            :src="wallet.fields['iosLogo']"
            class="mr-4 h-6 w-6 rounded-full object-cover ring-2 ring-white/20"
            alt="Logo wallet"
          />
          <div class="text-sm font-medium">{{ wallet.fields['merchantName'] }}</div>
        </div>
        <div class="space-y-3 px-4 py-2">
          <div class="text-lg font-medium">{{ wallet.fields['androidMandatoryTitle'] }}</div>
          <div class="flex flex-col">
            <span class="text-xs">{{ wallet.fields['iosPointsTitle'] }}</span>
            <span class="font-medium">10</span>
          </div>
          <div class="flex flex-col items-center space-y-2">
            <div
              :class="codeType.class"
              class="flex items-center justify-center bg-white text-center text-black shadow-md"
            >
              <span v-if="codeType.name === 'barcode' || !wallet.testUrl">{{
                t(`pages.loyaltySupports.wallet.config.${codeType.name}`)
              }}</span>
              <qrcode-vue v-else :value="wallet.testUrl" :size="100" level="L" />
            </div>
            <div v-if="codeType.name === 'barcode'" class="text-xs">cust_1a2b3c4d5e6f789</div>
            <div v-else-if="codeType.name === 'qrcode' && wallet.testUrl" class="text-xs">
              {{ t(`pages.loyaltySupports.wallet.config.scanMe`) }}
            </div>
          </div>
        </div>
        <div
          class="flex-1 rounded-b-3xl bg-white bg-cover bg-center bg-no-repeat"
          :style="{ backgroundImage: 'url(' + wallet.fields['iosImage'] + ')' }"
        ></div>
      </div>
      <div v-else-if="selectedView === 'details'" class="p-4">
        <img :src="wallet.fields['iosLogo']" class="h-10 w-10 rounded-full object-cover" alt="Logo wallet" />
        <div class="my-6 text-xl font-medium">{{ wallet.fields['androidMandatoryTitle'] }}</div>
        <div class="mb-4 space-y-0.5 text-xs">
          <p class="font-medium">{{ wallet.fields['rewardsTitle'] }}</p>
          <p>{{ user.merchant.webAppUrl }}</p>
        </div>
        <div class="space-y-0.5 border-b border-border/80 pb-4 text-xs">
          <p class="font-medium">{{ t('pages.loyaltySupports.wallet.config.preview.lastNotification') }}</p>
          <p>{{ t('pages.loyaltySupports.wallet.config.preview.lastNotificationContent') }}</p>
        </div>
      </div>
    </div>

    <div class="my-6 flex justify-center">
      <Tabs v-model="selectedView">
        <TabsList>
          <TabsTrigger value="wallet">{{ t('pages.loyaltySupports.wallet.config.preview.wallet') }}</TabsTrigger>
          <TabsTrigger value="details">{{ t('pages.loyaltySupports.wallet.config.preview.details') }}</TabsTrigger>
        </TabsList>
      </Tabs>
    </div>
    <div v-if="codeType.name === 'barcode' && wallet.testUrl" class="mb-4 flex justify-center">
      <Button variant="default-primary" @click="openUrl(wallet.testUrl)">
        {{ t(`pages.loyaltySupports.wallet.config.clickMe`) }}
      </Button>
    </div>
  </div>
</template>
